/**
 * Marque par entreprise : couleur d'accent et bornes du logo.
 *
 * Tout ce qui entre ici vient du serveur, qui l'a déjà validé — et tout est
 * revalidé quand même avant de finir dans un attribut `style`. Une couleur
 * est la seule valeur que le front injecte dans du CSS depuis une donnée ; la
 * forme fermée `#rrggbb` est la seule qu'on laisse passer.
 */
import type { CSSProperties } from "react";

/** La seule forme de couleur acceptée. */
export const HEX_COULEUR = /^#[0-9a-f]{6}$/i;

/** Types d'image acceptés pour un logo. Pas de SVG : il peut porter du script. */
export const LOGO_TYPES: readonly string[] = ["image/png", "image/jpeg", "image/webp"];
/** Taille maximale d'un logo, la même que côté serveur. */
export const LOGO_MAX_BYTES = 512 * 1024;

/** Luminance relative sRGB (WCAG 2.x). */
function luminance(hex: string): number {
  const canal = (i: number) => {
    const v = Number.parseInt(hex.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * canal(1) + 0.7152 * canal(3) + 0.0722 * canal(5);
}

/** Rapport de contraste WCAG entre deux couleurs `#rrggbb`. */
export function contrastRatio(hexA: string, hexB: string): number {
  const a = luminance(hexA);
  const b = luminance(hexB);
  const [clair, sombre] = a > b ? [a, b] : [b, a];
  return (clair + 0.05) / (sombre + 0.05);
}

/**
 * La couleur du texte posé SUR l'accent : blanc si le contraste tient le seuil
 * de 4,5:1, sinon l'encre sombre du thème. Une entreprise qui choisit un jaune
 * pâle garde des boutons lisibles.
 */
export function accentInk(hex: string): string {
  return contrastRatio(hex, "#ffffff") >= 4.5 ? "#ffffff" : "var(--lde-text)";
}

/** Mélange linéaire de deux couleurs `#rrggbb`, `part` étant la part de `hex`. */
function melange(hex: string, autre: string, part: number): string {
  const canal = (h: string, i: number) => Number.parseInt(h.slice(i, i + 2), 16);
  return (
    "#" +
    [1, 3, 5]
      .map((i) => Math.round(canal(hex, i) * part + canal(autre, i) * (1 - part)))
      .map((v) => v.toString(16).padStart(2, "0"))
      .join("")
  );
}

/**
 * L'accent employé comme texte sur fond clair : la couleur elle-même si elle
 * tient 4,5:1 sur blanc, sinon la même teinte assombrie par paliers jusqu'au
 * seuil. Un jaune de marque reste reconnaissable en lien, mais lisible.
 */
export function accentText(hex: string): string {
  let teinte = hex;
  for (let part = 0.9; contrastRatio(teinte, "#ffffff") < 4.5 && part > 0; part -= 0.1) {
    teinte = melange(hex, "#000000", part);
  }
  return teinte;
}

/**
 * Style à poser sur la racine d'un espace pour le teindre de l'accent d'une
 * entreprise. `undefined` sans couleur ou pour une couleur qui ne passe pas la
 * forme fermée : rien n'entre dans un `style` sans passer par `HEX_COULEUR`.
 *
 * Les SEPT variables sont redéclarées : `tokens.css` calcule `--lde-accent-soft`
 * et `--lde-accent-ring` par `color-mix()` sur `:root`, où `var(--lde-accent)`
 * est déjà résolu. Surcharger la seule `--lde-accent` sur un descendant ne les
 * recalculerait pas, et le fond des liens actifs resterait bleu.
 */
export function accentOverride(hex: string | null | undefined): CSSProperties | undefined {
  if (!hex || !HEX_COULEUR.test(hex)) {
    return undefined;
  }
  const couleur = hex.toLowerCase();
  return {
    "--lde-accent": couleur,
    "--lde-accent-strong": `color-mix(in srgb, ${couleur} 82%, black)`,
    "--lde-accent-dim": `color-mix(in srgb, ${couleur} 72%, white)`,
    "--lde-accent-soft": `color-mix(in srgb, ${couleur} 10%, transparent)`,
    "--lde-accent-ring": `color-mix(in srgb, ${couleur} 45%, transparent)`,
    "--lde-accent-ink": accentInk(couleur),
    "--lde-accent-text": accentText(couleur),
  } as CSSProperties;
}
